// The HUD of a tile's interior (PRD sections 19.6 and 19.7) in the "E · Oro suave" style, after the
// design system's iPad kit (ui_kits/islora-ipad/InteriorScreen.jsx): the «Isla» back button top left,
// the tile's header with its production top centre, the build bar at the bottom, and the placement
// buttons bottom right (Confirmar, Girar, Cancelar, with Deshacer and Rehacer always there). The time
// controls and the 3D/Cenital switch live in the shared top-right column (hud.ts).
import type { Rect } from '../render/camera-rig';
import { button, progressBar, squircle } from './controls';
import { h, icon } from './dom';
import { fmt } from './figures';
import { ICONS } from './icons';
import { itemIcon } from './item-icon';

/** What a build-bar button picks. */
export type BuildChoice = { readonly kind: 'belt' } | { readonly kind: 'machine'; readonly machine: string } | { readonly kind: 'remove' };

export interface BuildItem {
  readonly id: string;
  readonly label: string;
  readonly choice: BuildChoice;
  /** The item a machine makes, shown as its icon. */
  readonly product?: string;
}

export interface InteriorHudHandlers {
  readonly onBack: () => void;
  readonly onChoose: (choice: BuildChoice | undefined) => void;
  readonly onConfirm: () => void;
  readonly onRotate: () => void;
  readonly onCancel: () => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
}

export interface InteriorHudState {
  /** Build-bar button that is chosen, by id. */
  readonly chosen: string | undefined;
  /** The placement buttons: shown while placing; Confirmar only works when the ghost fits. */
  readonly placing: boolean;
  /** «Quitar» is chosen: only Cancelar applies. */
  readonly removing: boolean;
  readonly canConfirm: boolean;
  readonly canRotate: boolean;
  readonly hint: { readonly text: string; readonly tone: 'info' | 'ok' | 'amber' } | undefined;
  readonly undo: string | undefined;
  readonly redo: string | undefined;
}

export interface Production {
  /** Items per minute leaving through the exit, and what they are. */
  readonly rate: number;
  readonly item: { readonly id: string; readonly name: string } | undefined;
  /** Share of full speed of the machines, from 0 to 1. */
  readonly work: number;
}

export class InteriorHud {
  readonly el: HTMLElement;
  private readonly back: HTMLButtonElement;
  private readonly header: HTMLElement;
  private readonly title = h('span', { class: 'tile-header__title' });
  private readonly headerIcon = h('span', { class: 'tile-header__icon' });
  private readonly production = progressBar();
  private readonly bar: HTMLElement;
  private readonly buttons = new Map<string, HTMLButtonElement>();
  private readonly placement: HTMLElement;
  private readonly confirm: HTMLButtonElement;
  private readonly rotate: HTMLButtonElement;
  private readonly cancel: HTMLButtonElement;
  private readonly undo: HTMLButtonElement;
  private readonly redo: HTMLButtonElement;
  private readonly hint = h('div', { class: 'hint', role: 'status', 'aria-live': 'polite' });
  private productKey: string | undefined;

  constructor(items: readonly BuildItem[], handlers: InteriorHudHandlers) {
    this.back = h('button', { type: 'button', class: 'back-button', 'aria-label': 'Volver a la isla' }, squircle(icon(ICONS.caretLeft, 'icon back-button__icon'), { size: 64 }), h('span', { class: 'back-button__label' }, 'Isla'));
    this.back.addEventListener('click', handlers.onBack);

    this.header = h('header', { class: 'tile-header' }, this.headerIcon, h('span', { class: 'tile-header__text' }, this.title, this.production.el));

    let chosen: string | undefined;
    this.bar = h('nav', { class: 'build-bar', 'aria-label': 'Construir' });
    for (const item of items) {
      const glyph = item.product ? itemIcon(item.product, 40) : icon(item.choice.kind === 'belt' ? ICONS.fastForward : ICONS.close, `icon build-button__glyph build-button__glyph--${item.choice.kind}`);
      const b = h('button', { type: 'button', class: 'build-button', 'aria-pressed': 'false', 'data-build': item.id }, squircle(glyph, { size: 80, className: 'build-button__frame' }), h('span', { class: 'build-button__label' }, item.label));
      b.addEventListener('click', () => {
        chosen = this.buttons.get(item.id)?.getAttribute('aria-pressed') === 'true' ? undefined : item.id;
        handlers.onChoose(chosen === undefined ? undefined : item.choice);
      });
      this.buttons.set(item.id, b);
      this.bar.append(b);
    }

    this.confirm = button({ label: 'Confirmar', icon: ICONS.check, variant: 'primary', size: 'lg', onClick: handlers.onConfirm });
    this.rotate = button({ label: 'Girar', icon: ICONS.arrowsClockwise, variant: 'light', onClick: handlers.onRotate });
    this.cancel = button({ label: 'Cancelar', variant: 'ghost', onClick: handlers.onCancel });
    this.undo = button({ label: 'Deshacer', icon: ICONS.undo, variant: 'secondary', size: 'sm', onClick: handlers.onUndo });
    this.redo = button({ label: 'Rehacer', icon: ICONS.redo, variant: 'secondary', size: 'sm', onClick: handlers.onRedo });
    this.placement = h(
      'div',
      { class: 'placement' },
      h('div', { class: 'placement__ghost' }, this.confirm, this.rotate, this.cancel),
      h('div', { class: 'placement__history', role: 'group', 'aria-label': 'Historial' }, this.undo, this.redo),
    );

    this.el = h('div', { class: 'interior-hud', hidden: true }, this.back, this.header, this.hint, this.bar, this.placement);
  }

  /** The tile's name, terrain and size: «Taller · Prado 8×8». */
  setTitle(text: string): void {
    this.title.textContent = text;
  }

  setProduction(p: Production): void {
    const key = p.item?.id ?? '';
    if (key !== this.productKey) {
      this.productKey = key;
      this.headerIcon.replaceChildren(p.item ? itemIcon(p.item.id, 34) : icon(ICONS.cube, 'icon tile-header__cube'));
    }
    const label = p.item && Math.round(p.rate * 10) > 0 ? `${fmt(p.rate)} ${p.item.name.toLowerCase()} / min` : 'Aún no sale nada';
    this.production.set(p.work, label, p.work > 0 && p.work < 0.7 ? 'amber' : 'light');
  }

  render(state: InteriorHudState): void {
    for (const [id, b] of this.buttons) b.setAttribute('aria-pressed', String(id === state.chosen));
    this.el.classList.toggle('is-placing', state.placing);
    this.el.classList.toggle('is-removing', state.removing);
    this.confirm.disabled = !state.canConfirm;
    this.rotate.disabled = !state.canRotate;
    this.undo.disabled = state.undo === undefined;
    this.undo.setAttribute('aria-label', state.undo ? `Deshacer: ${state.undo}` : 'Deshacer');
    this.redo.disabled = state.redo === undefined;
    this.redo.setAttribute('aria-label', state.redo ? `Rehacer: ${state.redo}` : 'Rehacer');
    const hint = state.hint;
    this.hint.hidden = !hint;
    if (hint) {
      const className = `hint hint--${hint.tone}`;
      if (this.hint.className !== className) this.hint.className = className;
      const glyph = hint.tone === 'amber' ? ICONS.hourglass : hint.tone === 'ok' ? ICONS.checkCircle : ICONS.info;
      if (this.hint.dataset.text !== hint.text || this.hint.dataset.tone !== hint.tone) {
        this.hint.dataset.text = hint.text;
        this.hint.dataset.tone = hint.tone;
        this.hint.replaceChildren(icon(glyph, 'icon hint__icon'), h('span', {}, hint.text));
      }
    }
  }

  /**
   * The HUD panels the interior's camera keeps the block clear of. The placement buttons count even
   * while hidden, so choosing a tool never makes the camera jump.
   */
  obstacles(origin: DOMRect): Rect[] {
    const rect = (el: Element): Rect => {
      const r = el.getBoundingClientRect();
      return { left: r.left - origin.left, top: r.top - origin.top, right: r.right - origin.left, bottom: r.bottom - origin.top };
    };
    return [this.back, this.header, this.bar, this.placement].map(rect).filter((r) => r.right > r.left && r.bottom > r.top);
  }
}
